import Phaser from 'phaser';

import Sdk, { LeaderboardEntriesResponse, LeaderboardEntry } from '../sdk';
import i18n from '../../assets/i18n.json';
import debounce from '../utils/debounce';
import { Scene } from '../utils/consts';
import Button from '../button';
import HomeScene from './HomeScene';

const cellHeight = 48;
const fontSize = 32;
const itemHeight = 40;
const topMargin = 128;

let scale = 1;

export class LeaderboardScene extends Phaser.Scene {

    private resizeObserver?: ResizeObserver;
    private leaderboardData?: LeaderboardEntriesResponse;
    private tableContainer?: Phaser.GameObjects.Container;
    private texts: Record<string, string>;

    constructor() {
        super(Scene.LEADERBOARD);
        this.resizeObserver = new ResizeObserver(debounce(this.onResize.bind(this), 100));
        this.resizeObserver.observe(document.body);
    }

    create() {
        if (this.renderer.width < 700 || this.renderer.height < 700) {
            scale = 0.5;
        } else {
            scale = 1;
        }
        const sdk = this.registry.get('sdk') as Sdk;
        this.texts = i18n[sdk.getLang()];
        this.tableContainer = this.add.container(0, 0).setSize(this.renderer.width, this.renderer.height);
        this.onResize();
        this.tableContainer.add(this.add.text(this.tableContainer.width/2, 16, this.texts.leaderboard, {
            fontSize: 48,
            color: '#000',
            fontFamily: 'Trebuchet MS'
        }).setOrigin(0.5, 0));

        sdk.getPlayer().then(player => {
            if (player.getMode() === 'lite') {
                this.renderSelfScore();
            } else {
                this.loadLeaderboard().then((data => this.renderLeaderboard(data)));
            }
        });
    }

    private renderSelfScore() {
        const playerData = this.game.registry.get('playerData') || {};
        const topScore = playerData.topScore || 0;

        const height = cellHeight;
        const width = this.tableContainer!.width * 0.8;
        const leftMargin = this.tableContainer!.width * 0.1;
        const tableBackground = this.add.graphics()
            .setDefaultStyles({ lineStyle: { color: 0x000, width: 4 } })
            .fillStyle(0xf7f9fd)
            .fillRoundedRect(leftMargin - 24, topMargin - 24, width + 48, height + 48, 32)
            .strokeRoundedRect(leftMargin - 24, topMargin - 24, width + 48, height + 48, 32)
            .setName('tableBackground');
        const y = topMargin;

        this.tableContainer!.add([
            tableBackground,
            this.add.text(leftMargin + 32, y + itemHeight / 2 - fontSize / 2, this.texts.yourRecord, { fontFamily: 'Trebuchet MS', fontSize: fontSize + 'px', color: '#000' }),
            this.add.text(leftMargin + 300, y + itemHeight / 2 - fontSize / 2, topScore + '', { fontFamily: 'Trebuchet MS', fontSize: fontSize + 'px', color: '#000' }),

            this.add.text(this.tableContainer!.width / 2, cellHeight*2 + y + itemHeight / 2 - fontSize / 2, this.texts.notAuthorized, { fontFamily: 'Trebuchet MS', fontSize: fontSize + 'px', color: '#000' }).setOrigin(0.5),
        ]);

        const buttonHeight = this.renderMenuButton(height + topMargin);
        this.initDragging(height + topMargin + buttonHeight);
    }

    private async loadLeaderboard(): Promise<LeaderboardEntry[]> {
        this.leaderboardData = await (this.registry.get('sdk') as Sdk).getLeaderboardEntries();

        return this.leaderboardData.entries;
    }

    private renderLeaderboard(data: LeaderboardEntry[] = []) {
        if (!data.length) {
            throw new Error();
        }
        const width = this.tableContainer!.width * 0.8;
        const height = data.length * cellHeight;
        const leftMargin = this.tableContainer!.width * 0.1;
        
        const tableBackground = this.add.graphics()
            .setDefaultStyles({ lineStyle: { color: 0x000, width: 4 } })
            .fillStyle(0xf7f9fd)
            .fillRoundedRect(leftMargin - 24, topMargin - 24, width + 48, height + 48, 32)
            .strokeRoundedRect(leftMargin - 24, topMargin - 24, width + 48, height + 48, 32)
            .setName('tableBackground');

        this.tableContainer!.add(tableBackground);
        data.forEach((entry, i) => {
            const x = 0;
            const y = topMargin + cellHeight * i;

            const playerName = this.add.text(leftMargin + x + 300, y + itemHeight / 2 - fontSize / 2, entry.player.publicName, { fontFamily: 'Trebuchet MS', fontSize: fontSize + 'px', color: '#000' });
            if (playerName.width > width * 0.6) {
                playerName.setText(playerName.text.substring(0, Math.floor(playerName.text.length * (width * 0.5) / playerName.width)) + '...');
            }
            this.tableContainer!.add([
                this.add.text(leftMargin + x + 32, y + itemHeight / 2 - fontSize / 2, entry.rank + '', { fontFamily: 'Trebuchet MS', fontSize: fontSize + 'px', color: '#000' }),
                this.add.text(leftMargin + x + 150, y + itemHeight / 2 - fontSize / 2, entry.score + '', { fontFamily: 'Trebuchet MS', fontSize: fontSize + 'px', color: '#000' }),
                playerName
            ]);
        });

        const buttonHeight = this.renderMenuButton(height + topMargin);
        this.initDragging(height + topMargin + buttonHeight);
    }

    private renderMenuButton(tableBottom: number) {        
        const button = new Button(this, this.texts.menu, () => {
            this.game.scene.add(Scene.HOME, new HomeScene(), true);
            this.scene.remove();
        }, 'outline', 'XL').setPosition(this.tableContainer!.width / 2, tableBottom + 200)
        this.tableContainer?.add(button);

        return button.height + 200;
    }

    private initDragging(tableHeight: number) {
        this.tableContainer!.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.tableContainer!.width/scale, tableHeight/scale), Phaser.Geom.Rectangle.Contains);
        this.input.setDraggable(this.tableContainer!);
        this.input.dragDistanceThreshold = 16;
        this.tableContainer?.on('drag', (pointer, dragX, dragY) => {
            const deltaY = pointer.position.y - pointer.prevPosition.y;
            
            this.tableContainer!.setY(this.tableContainer!.y + deltaY);
        });
    }

    private onResize() {
        if (this.renderer.width < 700 || this.renderer.height < 700) {
            scale = 0.5;
        } else {
            scale = 1;
        }
        this.tableContainer!.setScale(scale).setSize(this.renderer.width/scale, this.renderer.height/scale);
    }
}
